import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { getDrivesByAgent, getDocumentsByAgent } from '../../../../services/agents';
import { listDocuments } from '../../../../services/document';
import { useAgent } from './AgentContext';

/**
 * DrivesContext - Maneja los drives Y documentos ASIGNADOS al agente
 * 
 * Este contexto contiene:
 * - Drives: Vaults/drives que tienen acceso al agente (EXCLUYENDO el vault propio del agente)
 * - Agent Documents: Documentos asignados directamente al agente (EXCLUYENDO los del vault propio)
 * 
 * Ambos se muestran en la tab "Drives" del KnowledgePage
 * 
 * NOTA: Filtra automáticamente el vault propio del agente y sus documentos
 * para evitar duplicados con la tab "Documents"
 */

const DrivesContext = createContext(null);

export const DrivesProvider = ({ children }) => {
  const { agentId, agent } = useAgent();
  const vaultUuid = agent?.vaultUuid;

  // Estado de Drives
  const [drives, setDrives] = useState([]);
  const [drivesLoading, setDrivesLoading] = useState(false);
  const [drivesError, setDrivesError] = useState(null);
  const [drivesFetched, setDrivesFetched] = useState(false);

  // Estado de Documentos del agente (asignados, NO del vault)
  const [agentDocuments, setAgentDocuments] = useState([]);
  const [agentDocumentsLoading, setAgentDocumentsLoading] = useState(false);
  const [agentDocumentsError, setAgentDocumentsError] = useState(null);
  const [agentDocumentsFetched, setAgentDocumentsFetched] = useState(false);

  // Refs para rastrear valores anteriores y detectar cambios
  const previousAgentIdRef = useRef(agentId);
  const previousVaultUuidRef = useRef(vaultUuid);

  // ============ DRIVES ============

  const fetchDrives = useCallback(async (force = false) => {
    if (!agentId) return;
    if (drivesFetched && !force) return;

    setDrivesLoading(true);
    setDrivesError(null);

    try {
      const response = await getDrivesByAgent(agentId);
      let drivesList = response.data?.body?.data || [];
      
      // ✅ Filtrar el vault propio del agente
      if (vaultUuid) {
        drivesList = drivesList.filter(
          (drive) => (drive.vaultUuid || drive.uuid || drive.id) !== vaultUuid
        );
      }
      
      setDrives(drivesList);
      setDrivesFetched(true);
    } catch (err) {
      console.error('Error fetching drives:', err);
      setDrivesError(err);
    } finally {
      setDrivesLoading(false);
    }
  }, [agentId, vaultUuid, drivesFetched]);

  const addDrive = useCallback((drive) => {
    setDrives((prev) => [...prev, drive]);
  }, []);

  const removeDrive = useCallback((driveId) => {
    setDrives((prev) => prev.filter((d) => 
      (d.vaultUuid || d.uuid || d.id) !== driveId
    ));
  }, []);

  const updateDrive = useCallback((driveId, updates) => {
    setDrives((prev) => prev.map((d) => {
      const id = d.vaultUuid || d.uuid || d.id;
      return id === driveId ? { ...d, ...updates } : d;
    }));
  }, []);

  // ============ AGENT DOCUMENTS (asignados al agente) ============

  const fetchAgentDocuments = useCallback(async (force = false) => {
    if (!agentId) return;
    if (agentDocumentsFetched && !force) return;

    setAgentDocumentsLoading(true);
    setAgentDocumentsError(null);

    try {
      const response = await getDocumentsByAgent(agentId);
      let documentsList = response.data?.data || [];
      
      // ✅ Obtener documentos del vault propio para filtrarlos
      let vaultDocumentUuids = new Set();
      
      if (vaultUuid) {
        try {
          const vaultResponse = await listDocuments({
            vault_uuid: vaultUuid,
            page_size: 100,
          });
          
          // ✅ Acceso correcto: listDocuments devuelve { body: [...] }
          const vaultDocs = vaultResponse.data?.body || [];
          
          // ✅ Crear Set con los documentUuid del vault (campo documentUuid)
          vaultDocumentUuids = new Set(
            vaultDocs.map((doc) => doc.documentUuid).filter(Boolean)
          );
        } catch (err) {
          console.warn('Error fetching vault documents for filtering:', err);
          // Si falla, continuamos sin filtrar (no es crítico)
        }
      }
      
      // ✅ Filtrar documentos que pertenecen al vault propio
      // getDocumentsByAgent devuelve documentos con campo 'uuid'
      if (vaultDocumentUuids.size > 0) {
        documentsList = documentsList.filter(
          (doc) => !vaultDocumentUuids.has(doc.uuid)
        );
      }
      
      setAgentDocuments(documentsList);
      setAgentDocumentsFetched(true);
    } catch (err) {
      console.error('Error fetching agent documents:', err);
      setAgentDocumentsError(err);
    } finally {
      setAgentDocumentsLoading(false);
    }
  }, [agentId, vaultUuid, agentDocumentsFetched]);

  const addAgentDocument = useCallback((document) => {
    setAgentDocuments((prev) => [...prev, document]);
  }, []);

  const removeAgentDocument = useCallback((documentId) => {
    setAgentDocuments((prev) => prev.filter((d) => 
      (d.uuid || d.id) !== documentId
    ));
  }, []);

  const updateAgentDocument = useCallback((documentId, updates) => {
    setAgentDocuments((prev) => prev.map((d) => {
      const id = d.uuid || d.id;
      return id === documentId ? { ...d, ...updates } : d;
    }));
  }, []);

  // ============ ACCIONES COMBINADAS ============

  const fetchAll = useCallback(async (force = false) => {
    await Promise.all([
      fetchDrives(force),
      fetchAgentDocuments(force),
    ]);
  }, [fetchDrives, fetchAgentDocuments]);

  const refetch = useCallback(() => {
    return fetchAll(true);
  }, [fetchAll]);

  const reset = useCallback(() => {
    setDrives([]);
    setDrivesLoading(false);
    setDrivesError(null);
    setDrivesFetched(false);
    setAgentDocuments([]);
    setAgentDocumentsLoading(false);
    setAgentDocumentsError(null);
    setAgentDocumentsFetched(false);
  }, []);

  // Resetear y recargar cuando cambia el agentId o vaultUuid (nuevo agente)
  useEffect(() => {
    const previousAgentId = previousAgentIdRef.current;
    const previousVaultUuid = previousVaultUuidRef.current;
    
    // Si cambió el agentId o vaultUuid, resetear estado y recargar
    if (previousAgentId !== agentId || previousVaultUuid !== vaultUuid) {
      previousAgentIdRef.current = agentId;
      previousVaultUuidRef.current = vaultUuid;
      
      // Resetear estado
      reset();
      
      // Recargar drives y documentos del nuevo agente si está disponible
      if (agentId) {
        fetchAll(true);
      }
    }
  }, [agentId, vaultUuid, reset, fetchAll]);

  // ============ ESTADO COMBINADO ============

  const loading = drivesLoading || agentDocumentsLoading;
  const error = drivesError || agentDocumentsError;
  const hasFetched = drivesFetched && agentDocumentsFetched;

  const value = useMemo(() => ({
    // Drives
    drives,
    drivesLoading,
    drivesError,
    drivesFetched,
    fetchDrives,
    addDrive,
    removeDrive,
    updateDrive,
    drivesCount: drives.length,

    // Agent Documents (asignados al agente)
    agentDocuments,
    agentDocumentsLoading,
    agentDocumentsError,
    agentDocumentsFetched,
    fetchAgentDocuments,
    addAgentDocument,
    removeAgentDocument,
    updateAgentDocument,
    agentDocumentsCount: agentDocuments.length,

    // Estado combinado
    loading,
    error,
    hasFetched,
    totalCount: drives.length + agentDocuments.length,

    // Acciones combinadas
    fetchAll,
    refetch,
    reset,
  }), [
    drives,
    drivesLoading,
    drivesError,
    drivesFetched,
    fetchDrives,
    addDrive,
    removeDrive,
    updateDrive,
    agentDocuments,
    agentDocumentsLoading,
    agentDocumentsError,
    agentDocumentsFetched,
    fetchAgentDocuments,
    addAgentDocument,
    removeAgentDocument,
    updateAgentDocument,
    loading,
    error,
    hasFetched,
    fetchAll,
    refetch,
    reset,
  ]);

  return (
    <DrivesContext.Provider value={value}>
      {children}
    </DrivesContext.Provider>
  );
};

export const useDrives = () => {
  const context = useContext(DrivesContext);
  if (!context) {
    throw new Error('useDrives must be used within DrivesProvider');
  }
  return context;
};

export default DrivesContext;
